import { FC } from 'react';
import { HStack, VStack } from '@chakra-ui/react';
import { formatActivityType } from '@shinetools/ape-code-library';

import Button from 'components/_core/Button';
import Card from 'components/_core/Card';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import { formatDate, PPatp } from 'helpers/date';
import { formatSIRET } from 'helpers/siret';

import locales from './locales';

type ResetOnboardingApplicationModalProps = Omit<
  SunshineModalProps,
  'children'
> & {
  onAbort: () => void;
  companyProfile: {
    activityType: string;
    legalForm: string;
    legalName: string;
    registrationDate: string;
    legalRegistrationNumber: string;
  };
};

const ResetOnboardingApplicationModal: FC<
  ResetOnboardingApplicationModalProps
> = ({ companyProfile, isOpen, onAbort, onClose }) => {
  const {
    activityType,
    legalForm,
    legalName,
    legalRegistrationNumber,
    registrationDate,
  } = companyProfile;

  return (
    <SunshineModal isOpen={isOpen} onClose={onClose}>
      <SunshineModal.Header />

      <SunshineModal.Body>
        <SunshineModal.Title>
          {locales.formatString(locales.title, {
            companyName: legalName as string,
          })}
        </SunshineModal.Title>
        <Text>{locales.subtitle}</Text>

        <Card marginBottom="space-8" marginTop="space-40" padding="0">
          <Card.Title
            backgroundColor="grey.100"
            borderBottomStyle="solid"
            borderBottomWidth="1px"
            borderColor="blackAlpha.100"
            borderTopRadius="radius-8"
            padding="space-16"
          >
            <VStack align="stretch" gap="space-4">
              <Text fontWeight="weight-500" size="md" variant="primary">
                {legalName}
              </Text>

              <Text size="sm" variant="secondary">
                {legalForm}
              </Text>
              <Text size="sm" variant="secondary">
                {formatActivityType(activityType)}
              </Text>
            </VStack>
          </Card.Title>
          <Card.Content
            flexDirection="row"
            gap="space-64"
            justifyContent="flex-start"
            padding="space-24"
          >
            <HStack>
              <SquircleIcon colorScheme="grey" name="profile" />
              <VStack align="stretch" gap="space-4">
                <Text fontWeight="weight-500" size="md" variant="primary">
                  {locales.registration}
                </Text>

                <Text size="sm" variant="secondary">
                  {formatDate(new Date(registrationDate), PPatp)}
                </Text>
              </VStack>
            </HStack>

            <HStack alignItems="start" gap="space-16">
              <SquircleIcon colorScheme="grey" name="company" />
              <VStack align="stretch" gap="space-4">
                <Text fontWeight="weight-500" size="md" variant="primary">
                  {locales.siret}
                </Text>

                <Text size="sm" variant="secondary">
                  {formatSIRET(legalRegistrationNumber)}
                </Text>
              </VStack>
            </HStack>
          </Card.Content>
        </Card>
      </SunshineModal.Body>
      <SunshineModal.Footer>
        <Button
          onClick={() => {
            onAbort();
            onClose();
          }}
          variant="inline-primary"
        >
          {locales.reset}
        </Button>
        <Button onClick={onClose}>{locales.cta}</Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default ResetOnboardingApplicationModal;
